import type {
  OrgPageSpecific,
  OrganizationListResult,
  Transaction,
  ListResponse,
  GrantCriteria,
  PointExpiryHistory,
  WhatsNewListResponse,
  TransactionExchange,
  PointHistoryGiftInfo,
  UserProfileSummary,
  TransactionGranted,
  ThankPointMessage,
  PointNotificationModel,
  ListCategories,
  Categories,
  HeaderInfo,
  FooterInfo,
  WhatsNewDetailsModel,
  CodesArea
} from "./models";

import {
  GiveThanksPointRequest,
  PatchedShoppingCartItemRequest,
  PersonalAddress,
  ShoppingCartItemApi,
  UserNotificationSettings,
  UserGrantCriteriaRankingApi,
  UserDashboardRankingApi,
  UserDashboardSummaryApi,
  UserPointStatusApi,
  ShoppingCartItemRequest,
  ShoppingCartPointSummaryApi,
  FooterApi,
  SystemNotificationsApi,
  RedemptionShippingAddressApi,
  RedemptionShippingAddressRequest,
  RedemptionApi,
  RedemptionRequestRequest,
  VoucherAddressApi,
  VoucherAddressRequest,
  MarkSystemNotificationsReadApi,
  InformationMessageTrackerRequest,
  OrganizationAdminSettingsApi,
  PatchedOrganizationAdminSettingsRequest,
  PersonalAddressRequest,
  PatchedRedemptionRequest,
  TaskProgressApi
} from "../../../gen/ricapi/api";

import {
  TransactionApi,
  ExpiringPointsHistoryApi,
  UserNotificationSettingsApi,
  OrganizationPageCustomizationApi,
  SearchThanksGivingByUserApi,
  ThanksGiving,
  ThanksGivingMessageApi,
  ThanksGivingMessageCreateRequest,
  ThanksGivingMessageList,
  ThanksPointApi,
  GrantCriteriaDetail,
  PersonalAddressApi,
  RandomUserAvatarApi
} from "../../../gen/ricapi/api";
import {
  OrganizationApi,
  TokenApi,
  RicAuthTokenObtainResponse,
  CustomUserApi,
  PasswordReminder,
  PasswordReminderApi,
  TokenRefreshRequest,
  EmailResetApi,
  PasswordReminderQuestionApi,
  SwitchSiteApi,
  SwitchSiteRequest,
  InitialInfoApi,
  InitialInfoRequestRequest,
  PatchedInitialInfoUUIDRequest,
  UserGroupRoleApi,
  BpfLoginApi,
  LoginApi
} from "../../../gen/authapi/api";

import {
  BrowsingHistoryApi,
  BrowsingHistoryRequest,
  FavoriteApi,
  FavoriteRequest,
  FeatureBannerApi,
  PremiumApi,
  PremiumCategoryApi,
  RankingByCategoryApi,
  RankingByPointsApi,
  SkuApi
} from "../../../gen/rssapi/api";

import { AxiosResponse } from "axios";
import { http as axios } from "../utils/http";
import LocalStorage from "@/general/utils/storage";

export async function getOrgSpecData(
  slug: string | undefined
): Promise<OrgPageSpecific> {
  const organizationApi = new OrganizationApi();

  const org: any = await (
    await organizationApi.organizationList(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      slug
    )
  ).data;

  const organizationID = org.results[0].id;

  const orgSpecDataApi = new OrganizationPageCustomizationApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response: any = await (
    await orgSpecDataApi.organizationPageCustomizationRetrieve(
      organizationID,
      undefined
    )
  ).data;

  return response as OrgPageSpecific;
}

export async function getOrganizationList(): Promise<OrganizationListResult> {
  const organizationApi = new OrganizationApi();
  const response: any = await organizationApi.organizationList();
  return response.data.results as OrganizationListResult;
}

export async function getGrantCriteriaList(
  ordering: string,
  page: number,
  pageSize = 20,
  search: string
) {
  const response: ListResponse<GrantCriteria> = await axios.get(
    "api/grant_criteria",
    {
      params: {
        ordering,
        page,
        page_size: pageSize,
        search
      }
    }
  );
  return response;
}

export async function getGrantCriteriaDetails(
  id: number,
  ordering: string,
  search: string
): Promise<GrantCriteriaDetail> {
  const GrantCriteriaDetailResponse: GrantCriteriaDetail = await axios.get(
    `api/grant_criteria/${id}`,
    {
      params: {
        ordering,
        search
      }
    }
  );
  return GrantCriteriaDetailResponse;
}

export async function getWhatsNewList(
  page: number,
  pageSize: number,
  search = "",
  month: string | undefined = undefined,
  year: string | undefined = undefined
): Promise<WhatsNewListResponse> {
  return await axios.get(`api/whats_new`, {
    params: {
      month,
      year,
      page,
      page_size: pageSize,
      search
    }
  });
}

export async function getWhatsNewDetails(
  id: number
): Promise<WhatsNewDetailsModel> {
  const response: WhatsNewDetailsModel = await axios.get(`api/whats_new/${id}`);

  return response;
}

export async function getContractInfos(key: string) {
  const loginApi = new LoginApi();
  const response = await (
    await loginApi.loginBaccountGetContractInfoRetrieve(key)
  ).data;
  return response;
}

export async function BpfLogin(
  key: string
): Promise<RicAuthTokenObtainResponse> {
  const bpfLogin = new BpfLoginApi();
  const response = await (await bpfLogin.bpfLoginCreate({ key })).data;
  return response as RicAuthTokenObtainResponse;
}
export async function login(
  username: string,
  password: string
): Promise<RicAuthTokenObtainResponse> {
  const tokenApi = new TokenApi();
  const response = await (
    await tokenApi.tokenCreate({ username, password })
  ).data;
  return response as RicAuthTokenObtainResponse;
}

export async function availableMonths() {
  const response = await axios.get(`api/list-available-months`);
  response.data = response;
  return response;
}

export async function getAccessTokenByRefreshToken(
  refreshToken: TokenRefreshRequest
): Promise<RicAuthTokenObtainResponse> {
  const tokenApi = new TokenApi();
  const response = await (await tokenApi.tokenRefreshCreate(refreshToken)).data;
  return response;
}
export async function getPointHistoryList(
  ordering: string,
  page: number,
  pageSize = 20,
  search: string,
  transaction_type: any,
  transaction_direction: any
) {
  const pointHistoryResponse: ListResponse<Transaction> = await axios.get(
    "api/transaction",
    {
      params: {
        ordering,
        page,
        page_size: pageSize,
        search,
        transaction_type,
        transaction_direction
      }
    }
  );
  return pointHistoryResponse;
}

export async function getGrantPointsDetails(
  id: number,
  ordering: string,
  search: string,
  transaction_type: any,
  transaction_direction: any
) {
  const pointHistoryApi = new TransactionApi();

  const Token = LocalStorage.getItem("AccessToken");
  const PointHistoryDetails = await (
    await pointHistoryApi.transactionRetrieve(
      id,
      ordering,
      search,
      transaction_type,
      transaction_direction,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return PointHistoryDetails;
}

export async function getTransactionExchange(id: number) {
  const pointTransactionExchange: TransactionExchange = await axios.get(
    `api/transaction_exchange/${id}`
  );
  return pointTransactionExchange;
}

export async function getTransactionThanksgivingDetail(
  id: number,
  transaction_direction: string
) {
  const transactionThanksgiving: PointHistoryGiftInfo = await axios.get(
    `api/transaction_thanksgiving/${id}`,
    {
      params: {
        transaction_direction
      }
    }
  );

  return transactionThanksgiving;
}

export async function getGrantedPointsDetails(id: number) {
  const grantedPointsDetails: TransactionGranted = await axios.get(
    `api/transaction_grant/${id}`
  );

  return grantedPointsDetails;
}

export async function getCustomUserDetails(id: number) {
  const customUserApi = new CustomUserApi();

  const Token = LocalStorage.getItem("AccessToken");
  const customUserDetails = await (
    await customUserApi.customUserRetrieve(id, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return customUserDetails;
}

export async function getUserProfile(search: string) {
  const userProfile = await axios.get(`api/user_profile_summary`, {
    params: {
      search
    }
  });
  LocalStorage.setItem("USER_POINTS", JSON.stringify(userProfile));
  userProfile.data = userProfile;
  return userProfile;
}

export async function getThankPointList(
  page: number | undefined,
  pageSize: number | undefined,
  search = "",
  user: string | undefined
) {
  const thankPointList = new ThanksPointApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await thankPointList.thanksPointList(
      page,
      pageSize,
      search,
      undefined,
      user,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function getThanksPointMessage(
  page: number,
  page_size: number | undefined,
  thanks_giving_id: number
) {
  // const thanksGivingMessageApi = new ThanksGivingMessageApi();
  // const Token = LocalStorage.getItem("AccessToken");
  // const response = await (
  //   await thanksGivingMessageApi.thanksGivingMessageList(
  //     latestExchange,
  //     ordering,
  //     page,
  //     pageSize,
  //     search,
  //     thanksGiving,
  //     {
  //       headers: { Authorization: "Bearer " + Token },
  //     }
  //   )
  // ).data;
  // return response;
  const response: ListResponse<ThankPointMessage> = await axios.get(
    `api/thanks_giving_message/${thanks_giving_id}`,
    {
      params: {
        page,
        page_size
      }
    }
  );
  return response;
}

export async function postThanksPointMessage(
  thanksGivingMessageCreate: ThanksGivingMessageCreateRequest
): Promise<ThanksGivingMessageList> {
  const thanksGivingMessageApi = new ThanksGivingMessageApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await thanksGivingMessageApi.thanksGivingMessageCreate(
      thanksGivingMessageCreate,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function getUserSelectionListForThanksPoint(
  excludeCurrentUser?: string,
  groupIds?: string | undefined,
  hiraganaName?: string,
  katakanaName?: string,
  nestedUsers?: string,
  ordering?: string,
  page?: number,
  pageSize?: number,
  roleType?: string,
  search?: string,
  searchPattern?: "user_info" | "user_and_group_info",
  username?: string,
  usingEmployeeId?: string
) {
  const customUserApi = new CustomUserApi();
  const Token = LocalStorage.getItem("AccessToken");
  let OrgSpecificData: any = LocalStorage.getItem("OrgSpecificData") as string;
  OrgSpecificData = JSON.parse(OrgSpecificData);
  const response = (
    await customUserApi.customUserList(
      excludeCurrentUser,
      groupIds,
      hiraganaName,
      undefined, //isDeleted
      katakanaName,
      nestedUsers,
      ordering,
      OrgSpecificData.organization,
      page,
      pageSize,
      roleType,
      search,
      "user_and_group_info",
      username,
      usingEmployeeId,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function postThanksPoint(
  giveThanksPoint: GiveThanksPointRequest
): Promise<AxiosResponse<ThanksGiving>> {
  const thanksPointApi = new ThanksPointApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = thanksPointApi.thanksPointCreate(giveThanksPoint, {
    headers: { Authorization: "Bearer " + Token }
  });

  return response;
}

export async function searchThanksPointByUser(
  page: number | undefined,
  pageSize: number | undefined,
  search: string | undefined
) {
  const searchThanksGivingByUserApi = new SearchThanksGivingByUserApi();
  //searchThanksGivingByUserApi.searchThanksGivingByUserList()
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await searchThanksGivingByUserApi.searchThanksGivingByUserList(
      page,
      pageSize,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function getUserProfileSummary() {
  const response: UserProfileSummary = await axios.get(
    "api/user_profile_summary"
  );
  return response;
}

export async function patchThanksGivingMessage(
  id: number,
  messageRead: boolean
) {
  const thanksGivingMessageApi = new ThanksGivingMessageApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await thanksGivingMessageApi.thanksGivingMessagePartialUpdate(
      id,
      undefined,
      undefined,
      { has_read: messageRead },
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}
export async function getExpirePointHistoryList(
  expiry_point_type: string,
  page: number,
  pageSize = 20,
  search: string
) {
  const response: ListResponse<PointExpiryHistory> = await axios.get(
    "api/expiring_points_history",
    {
      params: {
        expiry_point_type: expiry_point_type,
        page,
        search,
        page_size: pageSize
      }
    }
  );
  return response;
}

export async function getExpirePointDetails(
  expiry_point_type: string,
  id: number
) {
  const response: PointExpiryHistory = await axios.get(
    `api/expired-point-details/${id}`,
    {
      params: {
        expiry_point_type
      }
    }
  );
  return response;
}

export async function getProductList(
  page: number,
  pageSize: number,
  sort: string | undefined,
  min: number | undefined,
  max: number | undefined,
  categories: string | undefined,
  status: any,
  premiums: string | undefined
) {
  if (sort == "") {
    sort = undefined;
  }

  const skuApi = new SkuApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await skuApi.skuList(
      categories,
      max,
      min,
      page,
      pageSize,
      premiums,
      sort,
      status,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function productSearch(
  page: number,
  pageSize: number,
  sort: string | undefined,
  min: number | undefined,
  max: number | undefined,
  categories: string | undefined,
  search: string | undefined,
  premiums: string | undefined
) {
  if (sort == "") {
    sort = undefined;
  }
  const skuApi = new SkuApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await skuApi.skuListSearchRetrieve(
      categories,
      max,
      min,
      page,
      pageSize,
      premiums,
      search,
      sort,
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function getCategories() {
  const categoryApi = new PremiumCategoryApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await categoryApi.premiumCategoryList({
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

export async function getCategoryById(id: number) {
  const categoryApi = new PremiumCategoryApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await categoryApi.premiumCategoryRetrieve(id, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

export async function getPersonalAddressDetails() {
  const personalAddressApi = new PersonalAddressApi();

  const Token = LocalStorage.getItem("AccessToken");

  const PersonalAddressResponse = await (
    await personalAddressApi.personalAddressList(
      undefined,
      undefined,
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return PersonalAddressResponse;
}
export async function getPersonalAddressUpdate(
  id: number,
  personalAddress: PersonalAddress,
  search: string
) {
  const personalAddressApi = new PersonalAddressApi();

  const Token = LocalStorage.getItem("AccessToken");

  const PersonalAddressResponse = await (
    await personalAddressApi.personalAddressUpdate(
      id,
      personalAddress,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return PersonalAddressResponse;
}

export async function putPasswordReminder(
  id: number,
  passwordReminder: PasswordReminder
) {
  const passwordReminderApi = new PasswordReminderApi();

  const Token = LocalStorage.getItem("AccessToken");

  const PasswordReminderResponse = await (
    await passwordReminderApi.passwordReminderUpdate(id, passwordReminder, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return PasswordReminderResponse;
}

export async function getPasswordReminder(id: number) {
  const passwordReminderApi = new PasswordReminderApi();

  const Token = LocalStorage.getItem("AccessToken");

  const GetPasswordReminderResponse = await (
    await passwordReminderApi.passwordReminderRetrieve(id, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return GetPasswordReminderResponse;
}

export async function getUserNotificationDetails(id: number, search: string) {
  const userNotificationSettingsApi = new UserNotificationSettingsApi();

  const Token = LocalStorage.getItem("AccessToken");

  const UserNotificationSettingsResponse = await (
    await userNotificationSettingsApi.userNotificationSettingsRetrieve(
      id,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return UserNotificationSettingsResponse;
}

export async function postUserNotificationDetails(
  userNotificationSettings: UserNotificationSettings
) {
  const userNotificationSettingsApi = new UserNotificationSettingsApi();

  const Token = LocalStorage.getItem("AccessToken");

  const UserNotificationSettingsResponse = await (
    await userNotificationSettingsApi.userNotificationSettingsCreate(
      userNotificationSettings,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return UserNotificationSettingsResponse;
}

export async function putUserNotificationDetails(
  id: number,
  userNotificationSettings: UserNotificationSettings,
  search: string
) {
  const userNotificationSettingsApi = new UserNotificationSettingsApi();

  const Token = LocalStorage.getItem("AccessToken");

  const UserNotificationSettingsResponse = await (
    await userNotificationSettingsApi.userNotificationSettingsUpdate(
      id,
      userNotificationSettings,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return UserNotificationSettingsResponse;
}

export async function patchCustomUserDetail(
  id: number,
  bio: string | undefined,
  avatar: any
) {
  const customUserApi = new CustomUserApi();

  const Token = LocalStorage.getItem("AccessToken");

  const response = await (
    await customUserApi.customUserPartialUpdate(id, bio, avatar, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

export async function productDetail(id: number) {
  const premiumApi = new PremiumApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await premiumApi.premiumRetrieve(id, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

export async function getShoppingCartItems(
  page: number | undefined,
  pageSize: number | undefined,
  search: string | undefined
) {
  const shoppingCartItemApi = new ShoppingCartItemApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = (
    await shoppingCartItemApi.shoppingCartItemList(page, pageSize, search, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}
export async function getUserMonthlyCriteria(
  endDate: string,
  startDate: string,
  search: string | undefined
) {
  const userGrantCriteriaRankingApi = new UserGrantCriteriaRankingApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await userGrantCriteriaRankingApi.userGrantCriteriaRankingRetrieve(
      endDate,
      startDate,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}
export async function postShoppingCartItems(
  shoppingCartItemRequest: ShoppingCartItemRequest
) {
  const shoppingCartItemApi = new ShoppingCartItemApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = (
    await shoppingCartItemApi.shoppingCartItemCreate(shoppingCartItemRequest, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

export async function getUserMonthlyRanking(
  endDate: string,
  startDate: string,
  search: string | undefined
) {
  const userDashboardRankingApi = new UserDashboardRankingApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await userDashboardRankingApi.userDashboardRankingRetrieve(
      endDate,
      startDate,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}
export async function getShoppingCartPointSummary() {
  const shoppingCartPointSummaryApi = new ShoppingCartPointSummaryApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = (
    await shoppingCartPointSummaryApi.shoppingCartPointSummaryRetrieve(
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function getUserPointsTotal(search: string | undefined) {
  const userDashboardSummaryApi = new UserDashboardSummaryApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await userDashboardSummaryApi.userDashboardSummaryRetrieve(search, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}
export async function deleteItemFromShoppingCart(id: number) {
  const shoppingCartItemApi = new ShoppingCartItemApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = (
    await shoppingCartItemApi.shoppingCartItemDestroy(id, undefined, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

export async function getUserChartDetail(
  endDate: string,
  startDate: string,
  search: string | undefined
) {
  const userPointStatusApi = new UserPointStatusApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response = await (
    await userPointStatusApi.userPointStatusRetrieve(
      endDate,
      startDate,
      search,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function patchItemfromShoppingcart(
  id: number,
  patchShoppingCartRequest: PatchedShoppingCartItemRequest
) {
  const shoppingCartItemApi = new ShoppingCartItemApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = (
    await shoppingCartItemApi.shoppingCartItemPartialUpdate(
      id,
      undefined,
      patchShoppingCartRequest,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data;
  return response;
}

export async function getTopPageBanners() {
  const featureBannerApi = new FeatureBannerApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await featureBannerApi.featureBannerRetrieve({
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function getFooter() {
  const footerApi = new FooterApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await footerApi.footerList(undefined, undefined, undefined, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function getk1Notifications() {
  const notifications = new SystemNotificationsApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await notifications.systemNotificationsList(
    1,
    10,
    undefined,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function getk1NotificationDetails(id: number) {
  const notifications = new SystemNotificationsApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await notifications.systemNotificationsRetrieve(
    id,
    undefined,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function randomUsersAvatar() {
  const randomUsersAvatar = new RandomUserAvatarApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await randomUsersAvatar.randomUserAvatarList(undefined, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

/**
 *
 * @param has_read
 * @param ordering
 * @param page
 * @param pageSize
 * @param point_history_notification
 * @param search
 * @param top_page
 * @returns
 */
export async function getPointNotification(
  has_read?: string,
  ordering?: string,
  page?: number,
  pageSize?: number,
  point_history_notification?: string,
  search?: string,
  top_page?: string
) {
  const response: ListResponse<PointNotificationModel> = await axios.get(
    "api/point_notification",
    {
      params: {
        has_read,
        ordering,
        page,
        page_size: pageSize,
        point_history_notification,
        search,
        top_page
      }
    }
  );
  return response;
}

/**
 *
 * @param id
 * @returns
 */
export async function markNotificationAsRead(id: number) {
  const response = await axios.post(`api/point_notification`, {
    id,
    has_read: true
  });
  return response.data;
}

/**
 *
 * @param ids
 * @returns
 */
export async function updateAllPointNotification(ids: number[]) {
  const response = await axios.post(`api/point_notification/read-all`, {
    ids
  });
  return response.data;
}

export async function getFavorites() {
  const favoriteApi = new FavoriteApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await favoriteApi.favoriteList(1, 90, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function deleteFavorites(id: any) {
  const favoriteApi = new FavoriteApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await favoriteApi.favoriteDestroy(id, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function addToFavorite(favoriteRequest: FavoriteRequest) {
  const favoriteApi = new FavoriteApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await favoriteApi.favoriteCreate(favoriteRequest, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function headerCountSummary() {
  const userCountSummary: AxiosResponse = await axios.get(
    `api/user_count_summary`
  );
  userCountSummary.data = userCountSummary;
  return userCountSummary;
}

export async function browserHistory() {
  const browsingHistoryApi = new BrowsingHistoryApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await browsingHistoryApi.browsingHistoryList(1, 90, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function deleteBrowserHistory(id: number) {
  const browsingHistoryApi = new BrowsingHistoryApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await browsingHistoryApi.browsingHistoryDestroy(id, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function postBrowserHistory(
  browsingHistoryRequest: BrowsingHistoryRequest
) {
  const browsingHistoryApi = new BrowsingHistoryApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await browsingHistoryApi.browsingHistoryCreate(
    browsingHistoryRequest,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function rankingByCategory() {
  const rankingByCategoryApi = new RankingByCategoryApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await rankingByCategoryApi.rankingByCategoryRetrieve({
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function rankingByPoints() {
  const rankingByPointsApi = new RankingByPointsApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await rankingByPointsApi.rankingByPointsRetrieve({
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function postRedemptionShippingAddress(
  redemptionShippingAddressRequest: RedemptionShippingAddressRequest
) {
  const redemptionShippingAddressApi = new RedemptionShippingAddressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await redemptionShippingAddressApi.redemptionShippingAddressCreate(
      redemptionShippingAddressRequest,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function getredemptionShippingAddress() {
  const redemptionShippingAddressApi = new RedemptionShippingAddressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await redemptionShippingAddressApi.redemptionShippingAddressList(
    1,
    8,
    undefined,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function patchredemptionShippingAddress(
  id: number,
  redemptionShippingAddressRequest: RedemptionShippingAddressRequest
) {
  const redemptionShippingAddressApi = new RedemptionShippingAddressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await redemptionShippingAddressApi.redemptionShippingAddressUpdate(
      id,
      redemptionShippingAddressRequest,
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function postRedemption(
  redemptionRequest: RedemptionRequestRequest
) {
  const redemptionApi = new RedemptionApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await redemptionApi.redemptionCreate(redemptionRequest, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function getRedemptionPaymentGatewayActivation(
  redemptionId: number
) {
  const redemptionApi = new RedemptionApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await redemptionApi.redemptionPaymentGatewayActivationRetrieve(
      redemptionId,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function patchRedemptionPaymentGatewayStatusPartialUpdate(
  status: "cancel" | "ok",
  tid: string,
  param?: string | undefined,
  patchedRedemptionRequest?: PatchedRedemptionRequest
) {
  const redemptionApi = new RedemptionApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await redemptionApi.redemptionPaymentGatewayStatusPartialUpdate(
      status,
      tid,
      param,
      patchedRedemptionRequest,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function getTaskProgress(taskId: string) {
  const taskProgressApi = new TaskProgressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await taskProgressApi.taskProgressRetrieve(taskId, {
    headers: { Authorization: "Bearer " + Token }
  });

  return response.data;
}

export async function getVoucherAddress() {
  const voucherAddressApi = new VoucherAddressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await voucherAddressApi.voucherAddressList(1, 8, undefined, {
    headers: { Authorization: "Bearer " + Token }
  });
  return response.data;
}

export async function myPageEmailReset(EmailResetData: any) {
  const emailResetApi = new EmailResetApi();
  const Token = LocalStorage.getItem("AccessToken");

  const emailResetResponse = await emailResetApi.emailResetCreate(
    EmailResetData,
    { headers: { Authorization: "Bearer " + Token } }
  );
  return emailResetResponse.data;
}

export async function myPageEmailResetUUIDVerification(emailResetData: any) {
  const emailResetApi = new EmailResetApi();
  const Token = LocalStorage.getItem("AccessToken");

  const emailResetUUIDVerificationResponse =
    await emailResetApi.emailResetVerificationUuidPartialUpdate(
      emailResetData,
      { headers: { Authorization: "Bearer " + Token } }
    );
  return emailResetUUIDVerificationResponse.data;
}

export async function myPageEmailResetVerification(EmailResetData: any) {
  const emailResetApi = new EmailResetApi();
  const Token = LocalStorage.getItem("AccessToken");

  const emailResetVerificationResponse =
    await emailResetApi.emailResetVerificationPartialUpdate(EmailResetData, {
      headers: { Authorization: "Bearer " + Token }
    });
  return emailResetVerificationResponse.data;
}

export async function postVoucherAddress(
  voucherAddressRequest: VoucherAddressRequest
) {
  const voucherAddressApi = new VoucherAddressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await voucherAddressApi.voucherAddressCreate(
    voucherAddressRequest,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function patchVoucherAddress(
  id: number,
  voucherAddressRequest: VoucherAddressRequest
) {
  const voucherAddressApi = new VoucherAddressApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await voucherAddressApi.voucherAddressUpdate(
    id,
    voucherAddressRequest,
    undefined,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function getSecretQuestions() {
  const passwordReminderQuestionApi = new PasswordReminderQuestionApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await passwordReminderQuestionApi.passwordReminderQuestionList(
      undefined,
      undefined,
      undefined,
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function markK1NotificationAsRead(
  informationMessageTrackerRequest: InformationMessageTrackerRequest
) {
  const notifications = new MarkSystemNotificationsReadApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response = await notifications.markSystemNotificationsReadCreate(
    informationMessageTrackerRequest,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );
  return response.data;
}

export async function getOrganizationAdminSettings() {
  const organizationAdminSettingsApi = new OrganizationAdminSettingsApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await organizationAdminSettingsApi.organizationAdminSettingsList(
      undefined,
      undefined,
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function patchOrganizationAdminSettings(
  id: number,
  patchedOrganizationAdminSettingsRequest: PatchedOrganizationAdminSettingsRequest
) {
  const organizationAdminSettingsApi = new OrganizationAdminSettingsApi();
  const Token = LocalStorage.getItem("AccessToken");

  const response =
    await organizationAdminSettingsApi.organizationAdminSettingsPartialUpdate(
      id,
      "",
      patchedOrganizationAdminSettingsRequest,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    );
  return response.data;
}

export async function getUserNotificationList() {
  const userNotificationSettingsApi = new UserNotificationSettingsApi();

  const Token = LocalStorage.getItem("AccessToken");

  const UserNotificationSettingsResponse = await (
    await userNotificationSettingsApi.userNotificationSettingsList(
      undefined,
      undefined,
      undefined,
      {
        headers: { Authorization: "Bearer " + Token }
      }
    )
  ).data.results;
  return UserNotificationSettingsResponse;
}

export async function postSwitchSite(switchSiteRequest: SwitchSiteRequest) {
  const switchSiteApi = new SwitchSiteApi();

  const Token = LocalStorage.getItem("AccessToken");

  const response = await (
    await switchSiteApi.switchSiteCreate(switchSiteRequest, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return response;
}

/* Registration */

export async function initialInfo(
  initialInfoRequestRequest: InitialInfoRequestRequest
) {
  const initialInfoApi = new InitialInfoApi();

  const Token = LocalStorage.getItem("AccessToken");

  const response = await initialInfoApi.initialInfoCreate(
    initialInfoRequestRequest,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );

  return response?.data;
}

export async function initialInfoRegistration(
  patchedInitialInfoUUIDRequest: PatchedInitialInfoUUIDRequest
) {
  const initialInfoApi = new InitialInfoApi();

  const Token = LocalStorage.getItem("AccessToken");

  const response = await initialInfoApi.initialInfoRegistrationPartialUpdate(
    patchedInitialInfoUUIDRequest,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );

  return response?.data;
}

export async function getUserGroupRole(
  group?: string | undefined,
  ordering?: string | undefined,
  page?: number | undefined,
  pagesize?: number | undefined,
  role?: string | undefined,
  search?: string | undefined,
  user?: string | undefined
) {
  const userGroupRoleApi = new UserGroupRoleApi();

  const Token = LocalStorage.getItem("AccessToken");

  const response = await userGroupRoleApi.userGroupRoleList(
    undefined,
    group,
    ordering,
    page,
    pagesize,
    role,
    search,
    user,
    {
      headers: { Authorization: "Bearer " + Token }
    }
  );

  return response?.data;
}

export async function personalAddressCreate(
  personalAddress: PersonalAddressRequest
) {
  const personalAddressApi = new PersonalAddressApi();

  const Token = LocalStorage.getItem("AccessToken");

  const PersonalAddressResponse = await (
    await personalAddressApi.personalAddressCreate(personalAddress, {
      headers: { Authorization: "Bearer " + Token }
    })
  ).data;
  return PersonalAddressResponse;
}

export async function getOrganizationPageCustomizationDetails(id: any) {
  const orgSpecDataApi = new OrganizationPageCustomizationApi();
  const Token = LocalStorage.getItem("AccessToken");
  const response: any = await (
    await orgSpecDataApi.organizationPageCustomizationRetrieve(id, undefined)
  ).data;

  return response as OrgPageSpecific;
}

export async function getLargeCategories() {
  const response: ListCategories<Categories> = await axios.get(
    `v1/search/largeCategories`
  );
  return response;
}

export async function getHeaderInfo(): Promise<HeaderInfo> {
  return await axios.get(`/api/header`);
}

export async function getFooterInfo(orgId: string): Promise<FooterInfo> {
  return await axios.get(`/api/AdminFooter/${orgId}`);
}

export const getCodesArea = async (params?: {
  areaLrgClassCd: string;
  areaMidClassCd?: string;
  areaCmlClassCd?: string;
}): Promise<CodesArea> => {
  return await axios.get("/v1/enum/codes/area", { params });
};

export async function getLargeCodesArea(): Promise<
  { areaLrgClassCd: string; areaMidClassNm: string; areaMidClassCd: string }[]
> {
  const regionCodes = [
    "region-1",
    "region-2",
    "region-3",
    "region-4",
    "region-6",
    "region-7",
    "region-8",
    "region-9",
    "region-10",
    "region-11",
    "region-12",
    "region-13"
  ];

  const regions = await Promise.all(
    regionCodes.map((code) => getCodesArea({ areaLrgClassCd: code }))
  );

  const result = [];

  for (const region of regions) {
    const areaLrgClass = region?.areaLrgClasses?.[0];
    if (areaLrgClass?.areaMidClasses == null) {
      continue;
    }

    for (const item of areaLrgClass.areaMidClasses) {
      result.push({
        areaLrgClassCd: areaLrgClass.areaLrgClassCd,
        areaMidClassNm: item.areaMidClassNm,
        areaMidClassCd: item.areaMidClassCd
      });
    }
  }

  return result;
}
